"use client";

import Image from "next/image";
import { CSSProperties, useMemo, useRef } from "react";
import { z } from "zod";

import { cn, formatSectionTitle } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";

const MetadataSchema = z.object({
  id: z.union([z.number(), z.string()]),
  meta_image: z.string().optional().default(""),
  meta_alt_image: z.string().optional().default(""),
  meta_title: z.string().optional().default(""),
  meta_title_color: z.string().optional().default("#222222"),
  meta_sub_title: z.string().optional().default(""),
  meta_sub_title_color: z.string().optional().default("#505050"),
  meta_priority: z.coerce.number().optional().default(0),
});

const SectionSchema = z
  .object({
    title: z.string().optional().default(""),
    title_start_color: z.string().optional().default("#222222"),
    title_end_color: z.string().optional().default("#E15518"),
    description: z.string().optional().default(""),
    image: z.string().optional().default(""),
    mobile_image: z.string().optional().default(""),
    image_alt: z.string().optional().default(""),
    desktop_background_color: z.string().optional().default("#FFFFFF"),
    mobile_background_color: z.string().optional().default("#FFFFFF"),
    metadata: z.array(MetadataSchema).optional().default([]),
    status: z.union([z.boolean(), z.number()]).optional(),
  })
  .transform((d) => ({
    ...d,
    metadata: [...(d.metadata || [])].sort(
      (a, b) => (a.meta_priority || 0) - (b.meta_priority || 0),
    ),
  }));

type CardRow = z.infer<typeof MetadataSchema>;

function InfoRow({ row }: { row: CardRow }) {
  return (
    <div className="flex min-[1024px]:flex-row min-[768px]:flex-row flex-col min-[1024px]:items-center min-[768px]:items-start items-center min-[768px]:gap-[34px] gap-3">
      {row.meta_image && (
        <div className="relative shrink-0 min-[768px]:w-[60px] w-[36px] min-[768px]:h-[60px] h-[36px]">
          <Image
            src={row.meta_image}
            alt={row.meta_alt_image || row.meta_title || ""}
            fill
            className="object-contain"
          />
        </div>
      )}
      <div className="flex flex-col min-[768px]:gap-1.5 gap-1 items-center min-[768px]:items-start min-[1024px]:w-[450px] min-[768px]:w-[80%] w-[220px]">
        {row.meta_title && (
          <h3
            className="text-[16px] leading-[18px] min-[768px]:text-xl min-[768px]:leading-[26px] font-semibold min-[768px]:text-left text-center"
            style={{ color: row.meta_title_color || "#222222" }}
          >
            {row.meta_title}
          </h3>
        )}
        {row.meta_sub_title && (
          <p
            className="text-[14px] leading-normal font-normal min-[768px]:text-left text-center"
            style={{ color: row.meta_sub_title_color || "#505050" }}
          >
            {row.meta_sub_title}
          </p>
        )}
      </div>
    </div>
  );
}

export default function ProgramBenefitsPracticalSkills({ data }: { data: any }) {
  const ref = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  const raw = data?.data ?? data ?? {};
  const parsed = useMemo(() => SectionSchema.safeParse(raw), [raw]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_benefits_practical_skills");
  //   }
  // }, []);

  if (!parsed.success) {
    return null;
  }

  const section = parsed.data;
  const isOff =
    section.status === false ||
    section.status === 0 ||
    String(section.status) === "0";

  if (isOff) {
    return null;
  }

  const rows = section.metadata?.filter((m) => m.meta_title?.trim()) ?? [];
  const displayImage =
    (isMobile ? section.mobile_image : section.image)?.trim() ||
    section.image?.trim() ||
    "";

  const hasHeader =
    Boolean(section.title?.trim()) || Boolean(section.description?.trim());

  if (rows.length === 0 && !hasHeader && !displayImage) {
    return null;
  }

  const bgColor = isMobile
    ? section.mobile_background_color
    : section.desktop_background_color;

  return (
    <section ref={ref} className="w-full" style={{ backgroundColor: bgColor }}>
      <div className="min-[768px]:max-w-screen-container mx-auto relative min-[768px]:pt-[36px] min-[768px]:pb-[78px] pt-[53px] pb-[53px] min-[768px]:px-11 px-[26px] flex flex-col gap-6">
        {hasHeader && (
          <div className="flex flex-col min-[768px]:gap-4 gap-1.5 mx-auto">
            {section.title?.trim() && (
              <h2
                className="text-[28px] min-[768px]:text-[32px] font-semibold leading-tight text-center [&_span]:text-[color:var(--pb7-highlight)]"
                style={
                  {
                    color: section.title_start_color || "#222222",
                    "--pb7-highlight": section.title_end_color || "#E15518",
                  } as CSSProperties
                }
                dangerouslySetInnerHTML={{
                  __html: formatSectionTitle(section.title, "pb7-highlight"),
                }}
              />
            )}
            {section.description?.trim() && (
              <p className="whitespace-pre-wrap text-[14px] min-[768px]:text-[18px] min-[768px]:font-normal text-center min-[768px]:w-[482px] w-[330px] mx-auto">
                {section.description}
              </p>
            )}
          </div>
        )}

        <div className="flex min-[768px]:flex-row flex-col-reverse justify-between items-center min-[768px]:gap-0 gap-10">
          {rows.length > 0 && (
            <div className="flex flex-col gap-11">
              {rows.map((row) => (
                <InfoRow key={String(row.id)} row={row} />
              ))}
            </div>
          )}

          {displayImage && (
            <div className="relative min-[768px]:w-[358px] min-[768px]:h-[390px] w-[318px] h-[346px] aspect-[318/346]">
              <Image
                fill
                src={displayImage}
                alt={section.image_alt || ""}
                className="rounded-tl-4xl rounded-br-4xl min-[1024px]:w-[358px] min-[768px]:w-[300px] min-[768px]:h-[390px] object-cover"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
